import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { endOfDay, format, isAfter, parseISO } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";

import logoFlix from '../../assets/logoFlix.png';
import { useToast, useWindowDimensions } from '../../hooks';
import { downloadImage, getCustomErrorByLandingPage, loadImage } from '../../utils';
import {
    getDistributionRedemption,
    getDistributionRedemptionVouchers,
    getExhibitorLogo,
    getTemplateUrl
} from '../../services';
import { DistributionRedemption } from '../../dtos';
import { Loading, Snackbar } from '../../components';
import { CopyIcon, DownloadIcon } from '../../components/icons';
import { Button } from './components';
import {
    Container,
    Header,
    Content,
    VoucherContent,
    ButtonsRow,
    ExpiredContent,
    Instructions,
    CustomInnerHTML
} from './styles';

type CustomDistributionRedemption = DistributionRedemption & {
    exhibitorLogoUrl: string;
    templateImageUrl: string;
};

const RedemptionPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { isMobile } = useWindowDimensions();
    const { toast, showToast } = useToast();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [distributionRedemption, setDistributionRedemption] = useState<CustomDistributionRedemption | null>(null);
    const [codes, setCodes] = useState<string[]>([]);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [isBatchBlocked, setIsBatchBlocked] = useState<boolean>(false);
    const [isPrintOut, setIsPrintOut] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handlePdfDownload = async () => {
            try {
                if (!ref.current)
                    throw new Error('Ref is null!');

                const canvas = await html2canvas(ref.current, {
                    windowWidth: 210 * 4,
                    windowHeight: 297 * 4,
                    scale: 2
                });
                const base64 = canvas.toDataURL("image/png");

                const pdf = new jsPDF('p', 'mm', 'a4', true);
                const { getWidth: pdfWidth, getHeight: pdfHeight } = pdf.internal.pageSize;
                const { width: canvasWidth, height: canvasHeight } = canvas;
                const ratio = Math.min(pdfWidth() / canvasWidth, pdfHeight() / canvasHeight);

                pdf.addImage(base64, 'PNG', 0, 0, canvasWidth * ratio, canvasHeight * ratio);
                pdf.save('flixmediacortesias.pdf');
            } catch (err) {
                const customError = getCustomErrorByLandingPage({
                    err,
                    action: 'PDF_DOWNLOAD',
                    distributionRedemption,
                    codes
                });
                showToast({
                    type: 'error',
                    message: customError.message
                });
            } finally {
                setIsPrintOut(false);
                setTimeout(() => setLoading(false), 500);
            }
        };

        if (isPrintOut)
            handlePdfDownload();
    }, [showToast, isPrintOut, distributionRedemption, codes])

    const getVouchers = useCallback(async (distributionRedemption: DistributionRedemption) => {
        let codes: string[] = [];

        try {
            const distributionRedemptionVouchers = await getDistributionRedemptionVouchers(distributionRedemption.recipientHash);
            codes = distributionRedemptionVouchers.map(({ voucherCode }) => voucherCode);
            setCodes(codes);
        } catch (err) {
            const customError = getCustomErrorByLandingPage({
                err,
                action: 'LOAD_VOUCHERS',
                distributionRedemption,
                codes
            });
            showToast({
                type: 'error',
                message: customError.message
            });
        }
    }, [showToast]);

    useEffect(() => {
        const loadDistributionRedemption = async () => {
            let distributionRedemption: DistributionRedemption | null = null;

            try {
                setLoading(true);

                const hash = searchParams.get('hash');

                if (hash) {
                    distributionRedemption = await getDistributionRedemption(hash);

                    if (distributionRedemption) {
                        if (distributionRedemption.redeemedDate) {
                            await getVouchers(distributionRedemption);
                            setIsExpired(false);
                        } else {
                            const isExpired = isAfter(new Date(), endOfDay(parseISO(distributionRedemption.expiredDate)));
                            setIsExpired(isExpired);
                            const isBatchBlocked = distributionRedemption.batchStatus === 'INACTIVE';
                            setIsBatchBlocked(isBatchBlocked);
                        }

                        const templateImageUrl = getTemplateUrl(
                            distributionRedemption.templateUuid,
                            isMobile ? distributionRedemption.backgroundLPMobileFile : distributionRedemption.backgroundLPDesktopFile
                        );
                        const exhibitorLogoUrl = getExhibitorLogo(distributionRedemption.exhibitorUuid);

                        await loadImage(templateImageUrl);
                        const createdExhibitorLogoUrl = await downloadImage(exhibitorLogoUrl);

                        setDistributionRedemption({
                            ...distributionRedemption,
                            exhibitorLogoUrl: createdExhibitorLogoUrl,
                            templateImageUrl
                        });
                    } else {
                        navigate('/', {
                            replace: true
                        });
                    }
                } else {
                    navigate('/', {
                        replace: true
                    });
                }
            } catch (err) {
                const customError = getCustomErrorByLandingPage({
                    err,
                    action: 'LOAD_DISTRIBUTION',
                    distributionRedemption,
                    codes: []
                });
                showToast({
                    type: 'error',
                    message: customError.message
                });
            } finally {
                setLoading(false);
            }
        };

        loadDistributionRedemption();
    }, [navigate, getVouchers, showToast, searchParams, isMobile]);

    const redeemVouchers = useCallback(async (distribution: CustomDistributionRedemption) => {
        if (codes.length < 1) {
            setLoading(true);
            await getVouchers(distribution);
            setLoading(false);
        }
    }, [getVouchers, codes.length]);

    const copyCodes = useCallback(() => {
        navigator.clipboard.writeText(codes.join("\n"));
        showToast({
            type: 'success',
            message: 'Código(s) copiado(s) com sucesso!'
        });
    }, [showToast, codes]);

    return (
        <>
            <Loading loading={loading} webapp={false} />
            {toast && (
                <Snackbar layout="lp" {...toast} />
            )}
            <Container ref={ref}>
                {distributionRedemption && (
                    <>
                        <Header
                            $isPrintOut={isPrintOut}
                        >
                            <img src={logoFlix} alt="Flix Media" />
                            <img
                                src={distributionRedemption.exhibitorLogoUrl}
                                alt={distributionRedemption.exhibitorName}
                            />
                        </Header>
                        <Content
                            $isMobile={isMobile}
                            $isPrintOut={isPrintOut}
                            $templateUuid={distributionRedemption.templateUuid}
                            $templateImage={distributionRedemption.templateImageUrl}
                            $textColor={distributionRedemption.textColor}
                        >
                            <div>
                                {(isExpired || isBatchBlocked) ? (
                                    <ExpiredContent $isMobile={isMobile}>
                                        <h1>CORTESIA EXPIRADA</h1>
                                        <h2>{isBatchBlocked 
                                            ? 'Por favor, entre em contato com a Flix Media' 
                                            : 'Infelizmente a data para resgate da cortesia expirou'}</h2>
                                    </ExpiredContent>
                                ) : (
                                    <>
                                        <h1>{`${distributionRedemption.recipientName},`}</h1>
                                        <h2>Você ganhou <span>{distributionRedemption.recipientAmount}</span> Ticketx.</h2>
                                        <h4>Você deve utilizá-lo(s) na rede <span>{distributionRedemption.exhibitorName}</span> nas salas do tipo <span>{distributionRedemption.voucherTypeName}</span>.</h4>
                                        <CustomInnerHTML
                                            $textColor={distributionRedemption.textColor}
                                            $isPrintOut={isPrintOut}
                                            dangerouslySetInnerHTML={{
                                                __html: distributionRedemption.legalText
                                            }}
                                        />
                                        <VoucherContent
                                            $isMobile={isMobile}
                                            $isVouchersVisible={codes.length > 0}
                                            $isPrintOut={isPrintOut}
                                            $textColor={distributionRedemption.textColor}
                                            onClick={() => redeemVouchers(distributionRedemption)}
                                        >
                                            {codes.length < 1 ? (
                                                'Clique aqui para visualizar o(s) código(s)'
                                            ) : (
                                                <ul>
                                                    {codes.map(code => (
                                                        <li key={code}>{code}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </VoucherContent>
                                        <h5>Cada código vale um ingresso e são válidos até:&nbsp;
                                            <span>{format(parseISO(distributionRedemption.dueDate), 'dd/MM/yyyy')}</span>
                                        </h5>
                                        {codes.length > 0 && (
                                            <ButtonsRow $isMobile={isMobile} $isPrintOut={isPrintOut}>
                                                <Button
                                                    outlined
                                                    onClick={() => {
                                                        setLoading(true);
                                                        setIsPrintOut(true);
                                                    }}
                                                >
                                                    <DownloadIcon iconColor="#0472B7" />
                                                    Baixar PDF
                                                </Button>
                                                <Button
                                                    onClick={copyCodes}
                                                >
                                                    <CopyIcon iconColor="white" />
                                                    Copiar código(s)
                                                </Button>
                                            </ButtonsRow>
                                        )}
                                        <Instructions
                                            $textColor={distributionRedemption.textColor}
                                            $isPrintOut={isPrintOut}
                                            dangerouslySetInnerHTML={{
                                                __html: distributionRedemption.instructions
                                            }}
                                        />
                                        {codes.length > 0 && <h1>Aproveite o filme!</h1>}
                                    </>
                                )}
                            </div>
                        </Content>
                    </>
                )}
            </Container>
        </>
    );
};

export { RedemptionPage };
